import React from "react";
import { Link } from "react-router-dom";
import { AlertTriangle, FileCode2, Eye, Square, Wrench, ListChecks, Wand2, Copy, Zap } from "lucide-react";
import StreamingView from "./StreamingView";
import PlanCard from "./PlanCard";
import { modelBadge } from "../ModelSelect";
import { useCredits } from "../../context/CreditsContext";
import { assetUrl } from "../../lib/api";

const ModelTag = ({ id, used, credits }) => {
  const { models } = useCredits();
  const name = (k) => models.find((m) => m.id === k)?.label || k;
  const label = id === "auto" && used ? `Auto · ${name(used)}` : name(id);
  return <span className={`px-1.5 py-0.5 rounded border text-[10px] font-mono ${modelBadge(used || id)}`}>{label} · {credits}cr</span>;
};

const Thumbs = ({ items }) => (
  <div className="flex flex-wrap gap-1.5 mb-2" data-testid="chat-message-attachments">
    {items.map((a) => (
      <a key={a.id} href={assetUrl(a.url)} target="_blank" rel="noreferrer" className="w-16 h-16 rounded-lg overflow-hidden border border-slate-300 hover:border-sky-400">
        <img src={assetUrl(a.url)} alt={a.name || "attachment"} className="w-full h-full object-cover" />
      </a>
    ))}
  </div>
);

const Tag = ({ icon: Icon, children }) => <span className="inline-flex items-center gap-1 text-[10px] font-mono text-slate-500"><Icon size={10} />{children}</span>;

const UserMessage = ({ msg }) => {
  if (msg.kind === "autofix") {
    return (
      <div className="self-end max-w-[90%] rounded-2xl rounded-tr-sm px-4 py-3 border border-amber-200 bg-amber-50 fade-up" data-testid="chat-message-autofix">
        <div className="flex items-center gap-1.5 text-xs font-medium text-amber-700 mb-1.5"><Wrench size={12} /> Auto-fix · errors found in preview</div>
        <pre className="text-[11px] font-mono text-amber-900/80 whitespace-pre-wrap break-words max-h-28 overflow-hidden">{msg.content}</pre>
      </div>
    );
  }
  return (
    <div className="self-end max-w-[85%] bg-slate-100 text-slate-800 rounded-2xl rounded-tr-sm px-4 py-3 text-sm border border-slate-200 fade-up" data-testid="chat-message-user">
      {msg.attachments?.length > 0 && <Thumbs items={msg.attachments} />}
      <div className="whitespace-pre-wrap">{msg.content}</div>
      {(msg.mode === "plan" || msg.ai_images) && (
        <div className="mt-2 flex gap-3">
          {msg.mode === "plan" && <Tag icon={ListChecks}>plan first</Tag>}
          {msg.ai_images && <Tag icon={Wand2}>AI images</Tag>}
        </div>
      )}
    </div>
  );
};

const DoneBody = ({ msg, onPreviewVersion }) => (
  <>
    <p className="leading-relaxed whitespace-pre-wrap">{msg.content}</p>
    {(msg.files_changed?.length > 0 || msg.files_deleted?.length > 0) && (
      <div className="mt-3 bg-slate-50 p-3 rounded-lg border border-slate-200 space-y-1 text-xs font-mono">
        {msg.files_changed.map((f) => <div key={f} className="flex items-center gap-2 text-emerald-600"><FileCode2 size={12} /> {f}</div>)}
        {(msg.files_deleted || []).map((f) => <div key={f} className="flex items-center gap-2 text-rose-600 line-through"><FileCode2 size={12} /> {f}</div>)}
      </div>
    )}
    {msg.version_id && (
      <button onClick={() => onPreviewVersion(msg.version_id)} data-testid={`chat-version-chip-${msg.version_number}`}
        className="mt-3 inline-flex items-center gap-1.5 h-7 px-2.5 rounded-md border border-slate-300 text-xs text-slate-600 hover:text-slate-900 hover:border-sky-300 transition-colors">
        <Eye size={12} /> Version {msg.version_number}
      </button>
    )}
    {msg.needs_topup && (
      <Link to="/billing" data-testid="chat-needs-topup"
        className="mt-3 ml-2 inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-slate-900 text-white text-xs font-semibold hover:bg-slate-700 transition-colors">
        <Zap size={12} className="fill-amber-300 text-amber-300" /> Top up to finish
      </Link>
    )}
  </>
);

export const ChatMessage = ({ msg, latest, busy, onPreviewVersion, onApprovePlan }) => {
  if (msg.role === "user") return <UserMessage msg={msg} />;
  const isPlan = msg.kind === "plan" || msg.mode === "plan";
  return (
    <div className="self-start w-full max-w-[95%] bg-slate-50 text-slate-700 rounded-2xl rounded-tl-sm p-4 text-sm border border-slate-200 fade-up" data-testid={`chat-message-assistant-${msg.status}`}>
      <div className="mb-2.5 flex items-center gap-2">
        <span className="w-5 h-5 rounded-md bg-slate-900 text-white text-[11px] font-display font-bold flex items-center justify-center">i</span>
        <span className="text-xs font-medium text-slate-500">Ishukart</span>
        {msg.kind === "note" ? <span className="ml-auto"><Tag icon={Copy}>copy</Tag></span> : <span className="ml-auto"><ModelTag id={msg.model} used={msg.model_used} credits={msg.credits_used} /></span>}
      </div>
      {msg.status === "streaming" && <StreamingView raw={msg.raw || ""} plan={isPlan} stage={msg.stage} pendingImages={msg.images_pending} />}
      {msg.status === "error" && (
        <div className="flex gap-2 text-rose-600 text-sm" data-testid="chat-message-error"><AlertTriangle size={15} className="mt-0.5 shrink-0" /> {msg.content}</div>
      )}
      {msg.status === "stopped" && (
        <div className="flex gap-2 text-slate-500 text-sm" data-testid="chat-message-stopped"><Square size={13} className="mt-1 shrink-0" /> {msg.content}</div>
      )}
      {msg.status === "done" && (isPlan
        ? <PlanCard msg={msg} actionable={latest && !busy} onApprove={onApprovePlan} />
        : <DoneBody msg={msg} onPreviewVersion={onPreviewVersion} />)}
    </div>
  );
};

export default ChatMessage;
